import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import DraftManager from '../components/DraftManager';
import type { Submission, VerifyRecord } from '../types';

export default function SubmitArchive() {
  const [seedUrl, setSeedUrl] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once a draft is created we hand off to DraftManager; when it's submitted
  // for review (status flips), we show the confirmation.
  const [draft, setDraft] = useState<Submission | null>(null);

  async function createDraft(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api<{ data: Submission; verify_record: VerifyRecord | null }>('/archives/submit', {
        method: 'POST',
        body: {
          seed_url: seedUrl.trim(),
          submission_note: note.trim() || undefined,
        },
      });
      setDraft({ ...res.data, verify_record: res.verify_record });
      setSeedUrl('');
      setNote('');
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as { errors?: Record<string, string[]>; message?: string } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? `Submission failed (${err.status}).`);
      } else {
        setError('Submission failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Draft submitted for review → confirmation.
  if (draft && draft.status !== 'draft') {
    return (
      <div className="card" style={{ maxWidth: 600 }}>
        <h2>Thanks — we'll review your submission</h2>
        <p>
          <strong>{draft.domain}</strong> is now in the review queue. An admin
          will approve it for crawling or send it back with a reason. Track it on{' '}
          <Link to="/my-submissions">your submissions page</Link>.
        </p>
        <p>
          <button onClick={() => setDraft(null)}>Submit another site</button>
        </p>
      </div>
    );
  }

  // Draft created → finish setup (DNS proof + license + submit).
  if (draft) {
    return (
      <div className="card" style={{ maxWidth: 640 }}>
        <h2>Finish your submission</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Optionally prove you own the domain, then accept the license and
          submit for review. You can finish later from{' '}
          <Link to="/my-submissions">your submissions page</Link> — it's saved as a draft.
        </p>
        <DraftManager
          draft={draft}
          onChange={setDraft}
          onDeleted={() => setDraft(null)}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h2>Submit a site for archiving</h2>
      <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
        Propose a forum or community site for the archive. After you create the
        submission you'll optionally prove domain ownership and accept a short
        license; then it goes to an admin for review. See your past submissions on{' '}
        <Link to="/my-submissions">your submissions page</Link>.
      </p>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

      <form onSubmit={createDraft}>
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="seed_url" style={{ display: 'block', marginBottom: 4 }}>
            Seed URL
          </label>
          <input
            id="seed_url"
            type="url"
            required
            value={seedUrl}
            onChange={(e) => setSeedUrl(e.target.value)}
            placeholder="https://example.com/forum/"
            style={{ width: '100%' }}
            disabled={submitting}
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            The starting page. The crawler follows links from here within the same domain.
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="note" style={{ display: 'block', marginBottom: 4 }}>
            Why this site? <span className="muted" style={{ fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A note for the reviewer — what makes this site worth preserving, anything unusual about it, etc."
            rows={4}
            maxLength={2000}
            style={{ width: '100%', fontFamily: 'inherit' }}
            disabled={submitting}
          />
        </div>

        <button type="submit" disabled={submitting || !seedUrl.trim()}>
          {submitting ? 'Creating…' : 'Continue'}
        </button>
      </form>
    </div>
  );
}
